import { type GeneratedAsset, REFINE_CREDIT_COST } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Download, Info, Wand2 } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useRefineImage } from '~/features/ai-studio/hooks'
import { AiProcessingOverlay } from '~/features/shared/components/ai-processing-overlay'
import {
  CreditCostNote,
  CreditCostTag,
  useCreditCost,
} from '~/features/shared/components/credit-cost'
import { ImageUploadField } from '~/features/shared/components/image-upload-field'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'
import { Label } from '~/shared/ui/label'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { Textarea } from '~/shared/ui/textarea'
import { PageHeader } from '~/widgets/page-header'

const PROMPT_MAX_LENGTH = 500

function withExample(current: string, example: string): string {
  const base = current.trim()
  return base ? `${base}\n${example}` : example
}

const EXAMPLE_PROMPTS: { key: string; label: () => string; prompt: () => string }[] = [
  {
    key: 'remove-background',
    label: m.refineExampleRemoveBackground,
    prompt: m.refineExampleRemoveBackgroundPrompt,
  },
  {
    key: 'blur-background',
    label: m.refineExampleBlurBackground,
    prompt: m.refineExampleBlurBackgroundPrompt,
  },
  { key: 'brighten', label: m.refineExampleBrighten, prompt: m.refineExampleBrightenPrompt },
  { key: 'warm-light', label: m.refineExampleWarmLight, prompt: m.refineExampleWarmLightPrompt },
  {
    key: 'remove-objects',
    label: m.refineExampleRemoveObjects,
    prompt: m.refineExampleRemoveObjectsPrompt,
  },
  { key: 'retouch', label: m.refineExampleRetouch, prompt: m.refineExampleRetouchPrompt },
  { key: 'black-white', label: m.refineExampleBlackWhite, prompt: m.refineExampleBlackWhitePrompt },
  { key: 'film-look', label: m.refineExampleFilmLook, prompt: m.refineExampleFilmLookPrompt },
]

export function RefineTool() {
  const idPrefix = useId()
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const refine = useRefineImage(businessId)

  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [prompt, setPrompt] = useState('')
  const [result, setResult] = useState<GeneratedAsset | null>(null)

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  const { state: creditState } = useCreditCost(businessId, REFINE_CREDIT_COST)
  const canRefine =
    creditState !== 'short' &&
    !!file &&
    prompt.trim().length > 0 &&
    !!businessId &&
    !refine.isPending

  function loadFile(picked: File | null) {
    setFile(picked)
    setPreview(picked ? URL.createObjectURL(picked) : null)
    setResult(null)
  }

  async function handleRefine() {
    if (!file) return
    try {
      const res = await refine.mutateAsync({ file, prompt: prompt.trim() })
      setResult(res.asset)
      toast.success(m.refineDone())
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  return (
    <div className="space-y-6">
      <AiProcessingOverlay open={refine.isPending} />
      <PageHeader title={m.refineTool()} description={m.refineToolPageDescription()} />

      <Card>
        <CardContent className="space-y-5 pt-6">
          <div className="space-y-2">
            <Label>{m.refineSource()}</Label>
            <ImageUploadField file={file} preview={preview} onSelect={loadFile} />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-refine-prompt`}>{m.refinePromptLabel()}</Label>
            <Textarea
              id={`${idPrefix}-refine-prompt`}
              aria-describedby={`${idPrefix}-refine-hint`}
              value={prompt}
              maxLength={PROMPT_MAX_LENGTH}
              rows={3}
              placeholder={m.refinePromptPlaceholder()}
              onChange={(e) => setPrompt(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">{m.eventExamplePromptsLabel()}</span>
              {EXAMPLE_PROMPTS.map(({ key, label, prompt: example }) => {
                const next = withExample(prompt, example())
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={prompt.includes(example()) || next.length > PROMPT_MAX_LENGTH}
                    onClick={() => setPrompt(next)}
                    className="rounded-full border border-border/60 bg-muted/40 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border/60 disabled:hover:text-muted-foreground"
                  >
                    {label()}
                  </button>
                )
              })}
            </div>
            <p
              id={`${idPrefix}-refine-hint`}
              className="flex items-start gap-1.5 text-xs text-muted-foreground"
            >
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{m.refinePromptHint()}</span>
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-muted-foreground">
              <CreditCostNote businessId={businessId} cost={REFINE_CREDIT_COST} />
            </div>
            <Button disabled={!canRefine} onClick={handleRefine}>
              {refine.isPending ? (
                <PulsifyLoader className="size-5" tone="on-brand" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              {refine.isPending ? m.refineApplying() : m.refineApply()}
              {!refine.isPending && <CreditCostTag cost={REFINE_CREDIT_COST} />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div className="flex items-center justify-between">
              <p className="font-medium">{m.formatConverterResults()}</p>
              <Button asChild variant="link" size="sm">
                <Link to="/dashboard/media">{m.mediaLibrary()}</Link>
              </Button>
            </div>
            <div className="relative inline-block overflow-hidden rounded-lg bg-muted">
              <img src={result.url} alt="" className="max-h-64 object-contain" />
              <Button asChild size="icon" variant="secondary" className="absolute right-1 bottom-1">
                <a href={result.url} download target="_blank" rel="noreferrer">
                  <Download className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
